import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Modal, Paper, Switch } from '@mui/material';
import { getUser } from '../helpers/functions';
import { ptBR } from '@mui/x-data-grid/locales/';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { formatDate, formatDateTime } from '../helpers/format';
import { v4 as uuidv4 } from 'uuid';
import api from '../services/api';
import BasicDatePicker from '../components/DateInput';
import dayjs from 'dayjs';
import MultipleSelect from '../components/Multiselect';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';

function Dashboard() {
  const SEVENDAYSBEFORE = dayjs().subtract(7, 'day')
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0);
  const TODAY = dayjs()
    .set('hour', 23)
    .set('minute', 59)
    .set('second', 59)
    .set('millisecond', 59);
  const STARTDATEMONTH = dayjs().startOf('month');
  const ENDDATEMONTH = dayjs().endOf('month');
  const user = getUser();
  const [open, setOpen] = useState(false);
  const [exportList, setExportList] = useState([]);
  const [startDate, setStartDate] = useState(STARTDATEMONTH);
  const [endDate, setEndDate] = useState(ENDDATEMONTH);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedCompaniesDownload, setSelectedCompaniesDownload] = useState([]);
  const [exportXml, setExportXml] = useState(true);
  const [exportPdf, setExportPdf] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (value, func) => {
    func(value);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await getUserByUsername(user?.username);
        await getAllCompanies();
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async () => {
    try {
      const body = selectedCompanies.map((company) => ({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        userId: getUser()?.id,
        companyId: company.id,
        exportXml,
        exportPdf,
        isLoading: true,
      }));

      await api.post('/exports/register-list', body);
      showSuccess('Exportação solicitada com sucesso!');
      handleClose();
      await getExportList();
    } catch (err) {
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  }

  const handleClose = () => {
    setOpen(false);
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '876px',
    width: '90%',
    height: '500px',
    backgroundColor: '#1E1E1E',
    border: 'none',
    padding: '2rem',
    borderRadius: '4px',
  };

  const getUserByUsername = async (username) => {
    try {
      setLoading(true);
      const response = await api.get(`/users/${username}?isActive=true`);
      if (response?.data?.data?.UserCompanies?.length > 0) {
        const actualCompanies = response.data.data.UserCompanies.map(({ Company: { id, name } }) => ({ id, name }));
        delete response.data.data.UserCompanies;
        setSelectedCompanies(actualCompanies);
        await getExportList();
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    } finally {
      setLoading(false);
    }
  };

  const getAllCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      setCompanies(response?.data?.data ?? []);
    } catch (err) {
      setCompanies([]);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    } finally {
      setLoading(false);
    }
  };

  const getExportList = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: SEVENDAYSBEFORE,
        endDate: TODAY,
        userId: getUser()?.id,
      }
      const response = await api.get('/exports', { params });
      setExportList(response.data.data);
    } catch (err) {
      setExportList([]);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    } finally {
      setLoading(false);
    }
  };

  const showError = (message) => {
    const alert = <Alert
      className="error-content"
      variant="outlined"
      severity="error"
    >
      {message}
    </Alert>;
    setError(alert);
    setTimeout(() => setError(null), 3000);
  };

  const showSuccess = (message) => {
    const alert = <Alert
      className="error-content"
      variant="outlined"
      severity="success"
    >
      {message}
    </Alert>;
    setSuccess(alert);
    setTimeout(() => setSuccess(null), 3000);
  };

  const paginationModel = { page: 0, pageSize: 25 };

  const statusIcons = {
    'completed': (item) => <GridActionsCellItem
      icon={<ArrowCircleDownOutlinedIcon color="success" />}
      title={item.isCompleted ? 'Download disponível' : 'Aguardando o processo concluir'}
      label="Download"
      className="textPrimary"
      disabled={!item.isCompleted}
      onClick={() =>  download([{
        exportName: `${item?.id}_${item.Company.name.split(' ').join('_')}_${formatDate(item?.startDate, '-')}_${formatDate(item?.endDate, '-')}`,
        path: `${item?.id}_${item?.startDate}_${item?.endDate}`,
      }])}
      color="inherit"
    />,
    'empty': () => <GridActionsCellItem
      icon={<ArrowCircleDownOutlinedIcon color="disabled"/>}
      title="Não há dados disponíveis para download"
      label="Download"
      className="textPrimary"
      color="inherit"
    />,
    'error': () => <GridActionsCellItem
      icon={<ErrorOutlineRoundedIcon color="error" />}
      title="Ocorreu um erro no processo"
      label="Erro"
      className="textPrimary"
      color="inherit"
    />,
    'loading': () => <GridActionsCellItem
      icon={<CircularProgress color="inherit" size={18} />}
      title="Processo pendente"
      label="Carregando"
      className="textPrimary"
      color="inherit"
    />,
  }

  const columns = [
    {
      field: 'id',
      sortable: true,
      headerName: 'ID',
      width: 90,
      flex: 1,
    },
    {
      field: 'companyName',
      headerName: 'Empresa',
      width: 300,
      flex: 3,
      sortable: true,
      renderCell: (params) => params?.row?.Company?.name,
    },
    {
      field: 'period',
      headerName: 'Período',
      width: 200,
      flex: 3,
      renderCell: (params) => `${formatDate(params?.row?.startDate)} até ${formatDate(params?.row?.endDate)}`,
      sortable: true,
    },
    {
      field: 'export_pdf',
      headerName: 'PDF',
      width: 40,
      flex: 1,
      renderCell: (params) => <GridActionsCellItem
        icon={params?.row?.exportPdf ?
          <CheckCircleOutlineOutlinedIcon fontSize="small" color="success" /> :
          <HighlightOffOutlinedIcon fontSize="small" color="error" />
        }
        title="Processo pendente"
        label="Carregando"
        className="textPrimary"
        color="inherit"
      />,
      sortable: true,
    },
    {
      field: 'export_xml',
      headerName: 'XML',
      width: 40,
      flex: 1,
      renderCell: (params) => <GridActionsCellItem
        icon={params?.row?.exportXml ?
          <CheckCircleOutlineOutlinedIcon fontSize="small" color="success" /> :
          <HighlightOffOutlinedIcon fontSize="small" color="error" />
        }
        title="Processo pendente"
        label="Carregando"
        className="textPrimary"
        color="inherit"
      />,
      sortable: true,
    },
    {
      field: 'updatedAt',
      headerName: 'Data solicitação',
      sortable: true,
      width: 150,
      flex: 2,
      valueFormatter: formatDateTime,
    },
    {
      field: 'userName',
      headerName: 'Usuário',
      width: 200,
      flex: 2,
      sortable: true,
      renderCell: (params) => params?.row?.User?.name,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Status',
      width: 70,
      flex: 1,
      cellClassName: 'actions',
      getActions: (item) => {
        return [
          statusIcons[item?.row?.status](item.row)
        ];
      },
    }
  ];

  const prepareSelectedToDownload = () => {
    if (selectedCompaniesDownload.length === 0) {
      return showError('Não há exportações selecionadas para download');
    }
    let pathsToDownload = [];
    for (const item of exportList) {
      if (selectedCompaniesDownload.includes(item.id) && item.isCompleted) {
        pathsToDownload.push({
          exportName: `${item?.id}_${item.Company.name.split(' ').join('_')}_${formatDate(item?.startDate, '-')}_${formatDate(item?.endDate, '-')}`,
          path: `${item?.id}_${item?.startDate}_${item?.endDate}`,
        });
      }
    }
    if (pathsToDownload.length === 0) {
      return showError('Não há exportações disponíveis para download');
    }

    download(pathsToDownload);
  }

  const download = async (folderPaths) => {
    try {
      setLoading(true);
      const response = await api.post('/exports/download', folderPaths, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${uuidv4()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Algo de errado aconteceu');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {error}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <>
          {error}
          <Box style={style}>
            <h2 style={{ margin: '0 0 1rem' }}>Exportação de notas</h2>
            <div className="flex-column gap-05">
              <div className="flex-row gap-05">
                <BasicDatePicker
                  id="startDate"
                  value={startDate}
                  onChange={(value) => handleChange(value, setStartDate)}
                  label="Data início"
                />
                <BasicDatePicker
                  id="endDate"
                  value={endDate}
                  onChange={(value) => handleChange(value, setEndDate)}
                  label="Data fim"
                />
              </div>
              <MultipleSelect
                onChange={(value) => handleChange(value, setSelectedCompanies)}
                label="Empresas"
                description="name"
                loading={loading}
                data={companies}
                defaultData={selectedCompanies}
              />
              <div>
                <FormControlLabel
                  control={<Switch
                    disabled={loading}
                    name="exportXml"
                    onChange={(e) => handleChange(e.target.checked, setExportXml)}
                    checked={exportXml}
                    label=""
                    labelPlacement="left"
                  />}
                  sx={{ marginLeft: 0 }}
                  label="Exportar xml"
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={<Switch
                    disabled={loading}
                    name="exportPdf"
                    onChange={(e) => handleChange(e.target.checked, setExportPdf)}
                    checked={exportPdf}
                    label=""
                    labelPlacement="left"
                  />}
                  label="Exportar pdf"
                  labelPlacement="start"
                />
              </div>
            </div>
            <div id="modal-exportation" className="align-center" style={{ position: 'absolute', bottom: '2rem' }}>
              <Button
                sx={{ width: '150px', height: '40px', marginRight: '.5rem' }}
                variant="outlined"
                color="error"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                sx={{ width: '150px', height: '40px' }}
                variant="outlined"
                color="success"
                onClick={handleSubmit}
                disabled={loading}
              >
                { loading ? <CircularProgress size={20} /> : 'Exportar' }
              </Button>
            </div>
          </Box>
        </>
      </Modal>
      <div className="align-center justify-between">
        <h1>Exportações</h1>
        <div>
          <Button
            sx={{ width: '300px', height: '40px', marginRight: '.5rem' }}
            variant="outlined"
            color="primary"
            onClick={prepareSelectedToDownload}
            disabled={loading || selectedCompaniesDownload.length === 0}
          >
            {loading ?
              <CircularProgress color="inherit" size={18} sx={{ marginRight: '.5rem' }} /> :
              <ArrowCircleDownOutlinedIcon sx={{ marginRight: '.5rem' }} />
            } Baixar {selectedCompaniesDownload.length} selecionado(s)
          </Button>
          <Button
            sx={{ width: '150px', height: '40px' }}
            variant="outlined"
            color="primary"
            onClick={handleOpen}
            disabled={loading}
          >
            {loading ? <CircularProgress color="inherit" size={18} sx={{ marginRight: '.5rem' }} /> : ''}Exportar
          </Button>
        </div>
      </div>
      <Paper>
        <DataGrid
          density="compact"
          loading={loading}
          rows={exportList}
          columns={columns}
          checkboxSelection
          isRowSelectable={({row: { isCompleted, status }}) => isCompleted && status === 'completed'}
          onRowSelectionModelChange={(ids) => setSelectedCompaniesDownload(ids)}
          initialState={{
            pagination: { paginationModel },
            pinnedColumns: {
              left: ['desk'],
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          sx={{ border: 0 }}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        />
        {success}
      </Paper>
    </div>
  );
}

export default Dashboard;
